const rawGaId = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined;
// El valor `...` de .env.example no debe activar analítica por accidente.
const GA_ID = rawGaId?.match(/^G-[A-Z0-9]+$/i) ? rawGaId : undefined;

type EventParams = Record<string, string | number | boolean | undefined>;
type Gtag = (...args: unknown[]) => void;
type AnalyticsWindow = Window & { dataLayer?: unknown[]; gtag?: Gtag };

let initialized = false;
let pendingEvents: Array<{ eventName: string; params?: EventParams }> = [];
let lastPageKey: string | undefined;
let lastPageLocation: string | undefined;

function getAnalyticsWindow(): AnalyticsWindow {
	return window as AnalyticsWindow;
}

function sendEvent(eventName: string, params?: EventParams): void {
	if (!initialized) {
		pendingEvents.push({ eventName, params });
		return;
	}

	getAnalyticsWindow().gtag?.('event', eventName, params);
}

/** Inyecta gtag y desactiva el page_view automático. Llamar una vez desde el layout raíz. */
export function initGA(): void {
	if (initialized || !GA_ID || typeof window === 'undefined') return;
	initialized = true;

	const analyticsWindow = getAnalyticsWindow();
	analyticsWindow.dataLayer ??= [];
	// Mantener el formato exacto del snippet de Google: gtag.js consume entradas `arguments`.
	analyticsWindow.gtag ??= function () {
		analyticsWindow.dataLayer?.push(arguments);
	};
	analyticsWindow.gtag('js', new Date());
	analyticsWindow.gtag('config', GA_ID, { send_page_view: false });

	const script = document.createElement('script');
	script.async = true;
	script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
	document.head.appendChild(script);

	for (const event of pendingEvents) sendEvent(event.eventName, event.params);
	pendingEvents = [];
}

/** Envía un evento custom a GA4. */
export function trackEvent(eventName: string, params?: EventParams): void {
	if (!GA_ID || typeof window === 'undefined') return;
	sendEvent(eventName, params);
}

/**
 * Registra una vista virtual de Atlas. La misma pantalla consecutiva se ignora:
 * cambios de tab, heading o expansión de árbol no deben convertirse en page_view.
 */
export function trackPageView({
	pageKey,
	pageTitle,
	pageLocation
}: {
	pageKey: string;
	pageTitle: string;
	pageLocation: string;
}): void {
	if (!GA_ID || typeof window === 'undefined' || pageKey === lastPageKey) return;

	const pageReferrer = lastPageLocation ?? document.referrer;
	lastPageKey = pageKey;
	lastPageLocation = pageLocation;
	sendEvent('page_view', {
		page_title: pageTitle,
		page_location: pageLocation,
		page_referrer: pageReferrer
	});
}
